import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BackgroundIllustrations from '../components/common/BackgroundIllustrations';
import InputField from '../components/common/InputField';
import Button from '../components/common/Button';
import Logo from '../components/common/Logo';
import PasswordService from '../services/password.service';

const PasswordResetPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const email = queryParams.get('email');
  
  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  
  // Funzione per resettare l'errore
  const resetError = () => {
    setError('');
  };
  
  useEffect(() => {
    // Verifica che token ed email siano presenti
    if (!token || !email) {
      setError('Link non valido. Assicurati di usare il link completo ricevuto via email.');
      setValidatingToken(false);
      return;
    }
    
    // Verifica la validità del token
    const validateToken = async () => {
      try {
        const response = await PasswordService.validateToken(email, token);
        if (response.valid) {
          setTokenValid(true);
        } else {
          setError('Il link per il reset della password non è valido o è scaduto. Richiedi un nuovo link.');
        }
      } catch (err) {
        setError('Si è verificato un errore durante la validazione del link. Riprova più tardi.');
      } finally {
        setValidatingToken(false);
      }
    };
    
    validateToken();
  }, [token, email]);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    resetError();
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Verifica che le password corrispondano
    if (formData.password !== formData.password_confirmation) {
      setError('Le password non corrispondono');
      setLoading(false);
      return;
    }
    
    try {
      const response = await PasswordService.resetPassword(
        email, 
        token, 
        formData.password, 
        formData.password_confirmation
      );
      
      if (response.success) {
        setSuccess(response.message || 'Password reimpostata con successo. Verrai reindirizzato alla pagina di login.');
        // Reindirizza alla pagina di login dopo 3 secondi
        setTimeout(() => {
          navigate('/patient/login');
        }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Si è verificato un errore durante il reset della password. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };
  
  if (validatingToken) {
    return (
      <div className="relative min-h-screen flex flex-col">
        <BackgroundIllustrations className="absolute inset-0 z-0" />
        <div className="flex-grow flex items-center justify-center relative z-10">
          <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
            <Logo />
            <p className="mt-4">Verifica in corso del link di recupero password...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative min-h-screen flex flex-col">
      <BackgroundIllustrations className="absolute inset-0 z-0" />
      <div className="flex-grow flex items-center justify-center relative z-10">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <Logo />
          <h2 className="text-2xl font-bold mb-2 text-center">Reimposta Password</h2>
          
          {error && (
            <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}
          
          {!tokenValid && !success ? (
            <div className="text-center mt-4">
              <p className="mb-4">Il link non è valido o è scaduto.</p>
              <Button
                type="button"
                text="Torna al login"
                className="bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600"
                onClick={() => navigate('/patient/login')}
              />
            </div>
          ) : (
            !success && (
              <form onSubmit={handleSubmit}>
                <InputField
                  type="password"
                  name="password"
                  placeholder="Nuova password"
                  className="mb-4 w-full border border-orange-300 rounded-md p-2"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                
                <InputField
                  type="password"
                  name="password_confirmation"
                  placeholder="Conferma password"
                  className="mb-6 w-full border border-orange-300 rounded-md p-2"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required
                />
                
                <Button
                  type="submit"
                  text={loading ? "Elaborazione in corso..." : "Reimposta password"}
                  className="w-full bg-orange-500 text-white py-3 rounded-md hover:bg-orange-600 mb-6"
                />
              </form>
            )
          )}
          
          {!success && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/patient/login')}
                className="text-orange-500 hover:underline"
              >
                Torna al login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;